import {
  OutlinedInput,
  FormHelperText,
  OutlinedInputProps,
  styled,
  Box,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import InputTitle from '../common/InputTitle';
import { useState, useEffect } from 'react';
import { IoCloseCircleSharp } from 'react-icons/io5';
import FreeAnnouncementTooltip from '../common/FreeAnnouncementTooltip';

// Input Style 작업 중
const StyledInput = styled(OutlinedInput)(props => ({
  fontSize: '1.1rem',
  width: '100%',
  height: '2.5rem',
  marginBottom: '0.5rem',
  outline: 'none',
  '&.Mui-disabled.MuiInputBase-colorGray_1': {
    backgroundColor: '#f5f5f5',
    '& input': {
      WebkitTextFillColor: 'var(--gray_1)',
    },
    '& fieldset': {
      borderColor: 'var(--gray-2) !important',
    },
  },
  '&.Mui-error ': {
    '& fieldset': {
      borderColor: `${props.theme.palette.red.main} !important`,
    },
  },
  '&.Mui-focused': {
    fieldset: {
      borderWidth: '1px !important',
    },
  },
  '& input': {
    cursor: `${props.type === 'select' ? 'pointer' : 'auto'}`,
    padding: '0.5rem 1rem',

    '&::placeholder': {
      color: '#c7c7c7 !important',
      transition: 'none',
      fontWeight: '300',
      opacity: '1',
    },
    '&:disabled': {
      backgroundColor: '#f5f5f5 !important',
      cursor: 'default',
    },
  },

  '&.dot_password': {
    input: {
      WebkitTextSecurity: 'disc',
    },
  },

  '&:hover': {
    'input, fieldset': {
      borderColor: 'var(--gray-2)',
    },
  },
  '& fieldset': {
    borderColor: 'var(--gray-2) !important',
    legend: {
      outline: 'none',
    },
  },

  '.password_icon': {
    position: 'absolute',
    right: '0.9rem',
    width: '1.4rem',
    height: '1.4rem',
    cursor: 'pointer',
    color: '#bdbbbc',
  },
}));

interface CustomFormHelperTextProps {
  id?: string;
  toLongText?: boolean;
}

const CustomFormHelperText = styled(FormHelperText)<CustomFormHelperTextProps>(({ toLongText, id }) => {
  const top_bottom_position =
    id === 'ModalAccountLink'
      ? { bottom: '-1.5rem' }
      : id === 'direct_input'
      ? {}
      : toLongText
      ? { top: 'calc(15.25rem / 3)' }
      : { top: 'calc(2.25rem / 3)' };
  return {
    position: id === 'direct_input' ? 'relative' : 'absolute',
    right: '0',
    color: 'var(--red)',
    fontSize: '0.85rem',
    ...top_bottom_position,
  };
});

// select type의 오른쪽 아래 삼각형
const edit_triangle = {
  content: '',
  position: 'absolute',
  right: 0,
  bottom: '0.5rem',
  width: 0,
  height: 0,
  borderLeft: '7px solid transparent',
  borderRight: '7px solid #c2c2c2',
  borderTop: '7px solid transparent',
  borderBottom: '7px solid #c2c2c2',
  borderBottomRightRadius: '4px',
  marginBottom: '1px',
  cursor: 'pointer',
  zIndex: 1,
} as CSSProperties;

const CustomXIcon = styled(IoCloseCircleSharp)(() => ({
  width: '2.5rem',
  height: '2.5rem',
  color: '#c7c7c7',
  cursor: 'pointer',
  padding: '0.5rem',
  marginRight: '-0.5rem',
  position: 'absolute',
  top: '2.7rem',
  right: '1rem',
}));

const CloseEye = styled(IoMdEyeOff)(() => ({
  transform: 'scaleX(-1)',
}));

const OpenEye = styled(IoMdEye)(() => ({}));

// Input props Type
interface InputProps extends OutlinedInputProps {
  title?: string;
  sx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  tooltip?: {
    text: ReactElement;
    direction:
      | 'bottom'
      | 'left'
      | 'right'
      | 'top'
      | 'bottom-end'
      | 'bottom-start'
      | 'left-end'
      | 'left-start'
      | 'right-end'
      | 'right-start'
      | 'top-end'
      | 'top-start'
      | undefined;
  };
  required?: boolean;
  type?: 'text' | 'select' | 'password' | undefined | string;
  id?: string;
  icon?: ReactNode;
  errorText?: string;
  disable?: boolean;
  visibleButton?: boolean;
  classification?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  value?: string;
  error?: boolean;
  titleBold?: boolean;
  usePasswordIcon?: boolean;
  useDeleteIcon?: boolean;
  inputSx?: SxProps<Theme>;
  toLongText?: boolean;
  tutorial?: any;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onMounseDown?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>, type?: string) => void;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClickButtonName?: (type: string) => void;
  onClickButtonTime?: (type: string) => void;
  onClickDelete?: (type: string) => void;
}

const InputNormal = React.forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const title = props.title;
  const sx = props.sx;
  const tooltip = props.tooltip;
  const required = props.required;
  const input_type = props.type ? props.type : 'text';
  const placeholder = props.placeholder;
  const id = props.id;
  const disable = props.disable;
  const value = props.value;
  const visible_button = props.visibleButton;
  const classification = props.classification;
  const read_only = props.readOnly;
  const error = props.error;
  const error_text = props.errorText;
  const auto_focus = props.autoFocus;
  const onChange = props.onChange;
  const onClickButtonName = props.onClickButtonName;
  const onClickButtonTime = props.onClickButtonTime;
  const onFocus = props.onFocus;
  const onKeyDown = props.onKeyDown;
  const onKeyPress = props.onKeyPress;
  const onMouseDown = props.onMounseDown;
  const onBlur = props.onBlur;
  const onClickDelete = props.onClickDelete;
  const title_bold = props.titleBold;
  const title_sx = props.titleSx;
  const use_password_icon = props.usePasswordIcon;
  const use_delete_icon = props.useDeleteIcon;
  const input_sx = props.inputSx;
  const icon = props.icon;
  const tutorial = props.tutorial ? props.tutorial : { isOpen: false };

  const [inputType, setInputType] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [endIcon, setEndIcon] = useState<null | React.ReactNode>(null);

  const CloseEyeButton = () => {
    return (
      <CloseEye
        className='password_icon'
        onClick={() => {
          setPasswordVisible(true);
        }}
      />
    );
  };

  const OpenEyeButton = () => {
    return (
      <OpenEye
        className='password_icon'
        onClick={() => {
          setPasswordVisible(false);
        }}
      />
    );
  };

  useEffect(() => {
    if (input_type == 'password') {
      if (use_password_icon) {
        setEndIcon(<CloseEyeButton />);
      }
      setInputType('text');
    } else {
      setInputType(input_type);
    }
  }, [input_type]);

  useEffect(() => {
    if (!passwordVisible) {
      setEndIcon(<CloseEyeButton />);
    } else {
      setEndIcon(<OpenEyeButton />);
    }
  }, [passwordVisible]);

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event, classification);
    }
  };

  const removeData = () => {
    if (onClickDelete) {
      onClickDelete('ubirth');
    }
  };

  return (
    <>
      {title ? (
        <InputTitle
          title={title}
          tooltip={tooltip}
          required={required}
          visibleButton={visible_button ? visible_button : false}
          onClickButtonName={onClickButtonName ? onClickButtonName : undefined}
          onClickButtonTime={onClickButtonTime ? onClickButtonTime : undefined}
          classification={classification}
          sx={{ mb: '0', ...title_sx }}
          bold={title_bold}
          icon={icon}
        />
      ) : null}
      <Box sx={sx ? sx : { width: 1, position: 'relative' }}>
        {id && ['modal_pc_input'].includes(id) ? (
          <StyledInput
            id={id ?? 'input_normal'}
            name={id}
            type={inputType}
            placeholder={placeholder}
            color='gray_1'
            readOnly={inputType === 'select' || read_only ? true : false}
            disabled={disable ? true : false}
            value={value}
            onChange={handleInput}
            onClick={inputType === 'select' && !disable ? props.onClick : undefined}
            error={error}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onMouseDown={onMouseDown}
            autoFocus={auto_focus}
            onKeyPress={onKeyPress}
            ref={ref}
            sx={input_sx}
            endAdornment={input_type == 'password' ? endIcon : null}
            autoComplete={inputType == 'password' ? 'one-time-code' : 'off'}
            className={input_type == 'password' && !passwordVisible ? 'dot_password' : ''}
          />
        ) : (
          <FreeAnnouncementTooltip {...tutorial}>
            <StyledInput
              id={id ?? 'input_normal'}
              name={id}
              type={inputType}
              placeholder={placeholder}
              color='gray_1'
              readOnly={inputType === 'select' || read_only ? true : false}
              disabled={disable ? true : false}
              value={value}
              onChange={handleInput}
              onClick={inputType === 'select' && !disable ? props.onClick : undefined}
              error={error}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              onMouseDown={onMouseDown}
              autoFocus={auto_focus}
              onKeyPress={onKeyPress}
              ref={ref}
              sx={input_sx}
              endAdornment={input_type == 'password' ? endIcon : null}
              autoComplete={inputType == 'password' ? 'one-time-code' : 'off'}
              className={input_type == 'password' && !passwordVisible ? 'dot_password' : ''}
            />
          </FreeAnnouncementTooltip>
        )}
        {inputType === 'select' && id === 'payment_due_date' && value ? (
          <Box
            onClick={props.onClick}
            sx={{
              position: 'absolute',
              top: 'calc(50% - 0.25rem + 1px)',
              left: '1rem',
              color: '#d4d4d4',
              cursor: 'pointer',
              transform: 'translateY(-50%)',
              backgroundColor: '#fff',
            }}
          >
            <Typography component='span' sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
              {value}
            </Typography>{' '}
            (청구일 포함)
          </Box>
        ) : null}
        {inputType === 'select' && id === 'payment_period' && value ? (
          <Box
            onClick={props.onClick}
            sx={{
              height: 'calc(100% - 1rem)',
              position: 'absolute',
              top: '50%',
              left: '0',
              marginLeft: '1rem',
              textAlign: 'center',
              width: 'calc(100% - 2rem)',
              color: '#d4d4d4',
              cursor: 'pointer',
              transform: 'translateY(-50%)',
              backgroundColor: '#fff',
              fontSize: '1.1rem',
            }}
          >
            <Typography component='span' sx={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: '1.1rem' }}>
              {value}
            </Typography>{' '}
            (청구일 포함)
          </Box>
        ) : null}
        {inputType === 'select' ? <span className='input_triangle' style={edit_triangle}></span> : null}
        {use_delete_icon && value !== '' ? <CustomXIcon onClick={removeData} className='x_icon' /> : null}
        {error && error_text ? (
          <CustomFormHelperText id={id} toLongText={id === 'memno' && error_text.length > 34 ? true : false}>
            {error_text}
          </CustomFormHelperText>
        ) : null}
      </Box>
    </>
  );
});

InputNormal.displayName = 'InputNormal';

export default InputNormal;
